import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Badge, Box, Button, ListItemButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DashboardWidgetCard from '../DashboardWidgetCard';
import UserAvatar from '../../employee-chat/UserAvatar';
import { useEmployeeChat } from '../../../hooks/useEmployeeChat';
import SocketIOContext from '../../../context/socketIOContext';

const MAX_ENTRIES = 6;

// Compact relative label for the last activity of a conversation.
const timeLabel = value => {
  if (!value) return '';
  const d = dayjs(value);
  const now = dayjs();
  if (now.isSame(d, 'day')) return d.format('HH:mm');
  if (now.subtract(1, 'day').isSame(d, 'day')) return 'Gestern';
  return d.format('DD.MM.');
};

const previewOf = conversation => {
  const lm = conversation.lastMessage;
  if (!lm) return 'Neue Unterhaltung';
  if (lm.deleted) return 'Nachricht gelöscht';
  if (lm.text) return lm.text;
  if (lm.type === 'image') return '📷 Bild';
  if (lm.type === 'file') return '📎 Datei';
  return '';
};

// Dashboard widget: the user's most recent chat conversations with unread
// badges and live presence. Clicking an entry deep-links into the chat with
// that conversation opened.
export default function EmployeeChatWidget({ widget, editMode, onHide, dragHandleProps }) {
  const navigate = useNavigate();
  const socketCtx = useContext(SocketIOContext);
  const { getConversations } = useEmployeeChat();

  const [conversations, setConversations] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await getConversations();
      setConversations(res?.conversations || []);
      setError(null);
    } catch (_e) {
      setError('Unterhaltungen konnten nicht geladen werden.');
      setConversations(prev => prev || []);
    }
  }, [getConversations]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live updates: refresh (debounced) on chat activity, update presence dots
  // in place on presence events.
  useEffect(() => {
    if (!socketCtx?.on) return undefined;

    const scheduleRefresh = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(refresh, 500);
    };

    const offMessage = socketCtx.on('chat:message', scheduleRefresh);
    const offConversation = socketCtx.on('chat:conversation', scheduleRefresh);
    const offPresence = socketCtx.on('chat:presence', payload => {
      if (!payload?.userId) return;
      setConversations(prev =>
        prev
          ? prev.map(c =>
              c.otherUser?.id === String(payload.userId)
                ? { ...c, otherUser: { ...c.otherUser, isOnline: !!payload.online } }
                : c
            )
          : prev
      );
    });

    return () => {
      offMessage();
      offConversation();
      offPresence();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [socketCtx, refresh]);

  const list = (conversations || [])
    .filter(c => !c.archived)
    .slice(0, MAX_ENTRIES);
  const totalUnread = (conversations || []).reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  return (
    <DashboardWidgetCard
      title={widget.title}
      icon={widget.icon}
      accent={widget.accent}
      link='/mitarbeiter-chat'
      loading={conversations === null}
      error={error}
      onRetry={refresh}
      empty={conversations !== null && list.length === 0}
      emptyText='Noch keine Unterhaltungen — starte einen Chat mit deinen Kollegen.'
      editMode={editMode}
      onHide={onHide}
      dragHandleProps={dragHandleProps}
      footer={
        <Button
          size='small'
          startIcon={<ChatBubbleOutlineIcon />}
          onClick={() => navigate('/mitarbeiter-chat')}
          sx={{ textTransform: 'none' }}
        >
          {totalUnread > 0
            ? `${totalUnread} ungelesene Nachricht${totalUnread === 1 ? '' : 'en'}`
            : 'Chat öffnen'}
        </Button>
      }
    >
      <Box sx={{ mx: -1 }}>
        {list.map(conversation => {
          const other = conversation.otherUser || {};
          const name = other.displayName || 'Mitarbeiter';
          return (
            <ListItemButton
              key={conversation.id}
              onClick={() => navigate(`/mitarbeiter-chat?conversation=${conversation.id}`)}
              sx={{ borderRadius: 1, px: 1, py: 0.75, gap: 1.25 }}
            >
              <UserAvatar name={name} size={36} online={!!other.isOnline} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography
                    variant='body2'
                    noWrap
                    sx={{ flex: 1, fontWeight: conversation.unreadCount ? 700 : 500 }}
                  >
                    {name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {timeLabel(conversation.lastMessageAt)}
                  </Typography>
                </Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  noWrap
                  sx={{ display: 'block', fontWeight: conversation.unreadCount ? 600 : 400 }}
                >
                  {previewOf(conversation)}
                </Typography>
              </Box>
              {conversation.unreadCount > 0 && (
                <Badge
                  color='error'
                  badgeContent={conversation.unreadCount}
                  max={99}
                  sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
                />
              )}
            </ListItemButton>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
}
